import { useMemo } from "react";
import { IGenericRequestParameters, InferSortByOptions, StringIndexedDict } from "../../../api/GenericTypes";
import {
  EntitySidebarTile,
  EntitySidebarTileProps,
} from "../../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTile";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { PropertyTranslator } from "../../../api/GenericTranslator";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { NotAllowed, NotSet } from "../../../common/misc/UIconstants";
import { GetPersons } from "../../../common/misc/EntityRenders/EntityRenderer";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { AuditLog } from "../../../api/AuditLog";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { eventIdToInfo } from "../../../AuditLog/AuditLog";

export const GenericEntityTile = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  componentProps,
}: GenericEntityComponentProps<Entity, EntitySidebarTileProps<Entity, Filters>>) => {
  const suggestedEntityProps = useDefaultGenericEntityTile({ entityTypeId });
  const defaultComponentProps = useMemo(() => {
    const { entityConstants, sortingOptions, filtersTranslator } = genericEntityConstants[entityTypeId];
    return {
      // Generic values
      entityConstants,
      orderByOptions: sortingOptions,
      translatorConsts: filtersTranslator as PropertyTranslator<Filters>,
      rowLabel: (entity: Entity) => entity?.name ?? NotSet,
      rowDate: (entity: Entity) =>
        !!entity && Object.hasOwn(entity ?? {}, "modifiedOn") ? <DateTimeRenderer date={entity.modifiedOn} /> : NotSet,
      rowInfo: (entity: Entity) =>
        !!entity && Object.hasOwn(entity ?? {}, "modifiedBy") ? (
          <GetPersons persons={entity.modifiedBy} createLinks={false} />
        ) : (
          NotSet
        ),
      // Default overrides
      ...suggestedEntityProps,
      // Local overrides
      ...componentProps,
      // Forced props
    };
  }, [componentProps, entityTypeId, suggestedEntityProps]);

  const { canReadIndex } = useEntityPermissions({ entityTypeId });
  if (!canReadIndex) return NotAllowed;
  return <EntitySidebarTile<Entity, Filters> {...(defaultComponentProps as any)} />;
};

const useDefaultGenericEntityTile: <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
}: {
  entityTypeId: GenericEntityConstantsEntities;
}) => Partial<EntitySidebarTileProps<Entity, Filters>> = ({ entityTypeId }) => {
  switch (entityTypeId) {
    case "auditLogs":
      return {
        rowLabel: (entity) => (
          <DateTimeRenderer date={(entity as any as AuditLog).timestamp} includeDate includeElapsed includeTime />
        ),
        rowInfo: (entity) => {
          const eventInfo = eventIdToInfo((entity as any as AuditLog).eventId);
          return (
            <div className={`label label-soft-${eventInfo.color}`}>
              {!!eventInfo.icon && <LucideIcon name={eventInfo.icon} />} {eventInfo.label}
            </div>
          );
        },
        rowDate: (entity) => (
          <div className="flex align-center gap-5">
            by
            <GetPersons persons={(entity as any as AuditLog).user} createLinks />
          </div>
        ),
      };
    default:
      return {};
  }
};
